import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "@/lib/utils"; // Assumes a standard `cn` utility for merging classes

// 1. --- PROPS INTERFACE ---
// The component is now driven by props, making it reusable.
interface FluidTextMorphProps {
  wordPairs: [string, string][];
  className?: string;
  // Allows for custom animation props to be passed
  animationProps?: {
    initialColor?: string;
    animateColor?: string;
    exitColor?: string;
  };
}

// 2. --- REUSABLE COMPONENT LOGIC ---
// The component name is capitalized as per React standards.
export function FluidTextMorph({
  wordPairs,
  className,
  animationProps = {},
}: FluidTextMorphProps) {
  const [counter, setCounter] = useState(0);

  // Default colors are now CSS variables for theming
  const {
    initialColor = "hsl(var(--primary))",
    animateColor = "hsl(var(--foreground))",
    exitColor = "hsl(var(--destructive))",
  } = animationProps;

  useEffect(() => {
    const interval = setInterval(() => {
      setCounter((prev) => prev + 1);
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const currentPairIndex = Math.floor(counter / 2) % wordPairs.length;
  const currentWordIndex = counter % 2;
  const word = wordPairs[currentPairIndex][currentWordIndex];

  const letters = word.split("");

  // 3. --- CLEAN, REUSABLE MARKUP ---
  return (
    <div
      className={cn(
        "relative flex items-center justify-center text-6xl font-bold sm:text-8xl",
        className
      )}
    >
      <AnimatePresence mode="wait">
        {letters.map((letter, i) => (
          <motion.span
            key={`letter-${i}-${letter}`}
            layoutId={`letter-${i}`}
            // 4. --- THEMING WITH CSS VARIABLES ---
            initial={{ opacity: 0, y: 30, scale: 0.8, color: initialColor }}
            animate={{
              opacity: 1,
              y: 0,
              scale: 1,
              color: animateColor,
              transition: {
                type: "spring",
                damping: 15,
                stiffness: 200,
                delay: i * 0.05,
              },
            }}
            exit={{
              opacity: 0,
              y: -30,
              scale: 0.8,
              color: exitColor,
              transition: {
                type: "spring",
                damping: 15,
                stiffness: 200,
                delay: (letters.length - 1 - i) * 0.05,
              },
            }}
            className="relative"
          >
            {letter}
          </motion.span>
        ))}
      </AnimatePresence>
    </div>
  );
}
